import React from 'react';
import PropTypes from 'prop-types';
import ShippingThresholdHeader from 'yoda-checkout-components/lib/components/ShippingThreshold/ShippingThresholdHeader';
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import {
    selectDeviceType,
    selectFeatureFlags,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import OrderSummary from 'yoda-checkout-components/lib/components/OrderSummary/OrderSummary';
import styles from '../../containers/Pages/Cart/CartPageWrapper/CartPageWrapper.css';

const cx = classNames.bind(styles);

function OrderSummaryWrapperMobile({
    isNotAGiftCardOrder,
    handleApplyRewardsSection,
    displayShippingThresholdHeader,
    isShippingFree,
    removeCouponSummary,
    idSuffix,
}) {
    const deviceType = useSelector(selectDeviceType);
    const { isDesktop } = deviceType;
    const freeShippingThreshold =
        useSelector((state) => state.orderInfo?.freeShippingThreshold) || 0;
    const irisShippingThresholds = useSelector(
        (state) => state.irisPromotionalBannerData?.shippingThresholds || {}
    );
    const applePayEligible = useSelector((state) => state.cartDetails?.applePayEligible);
    const orderTotal =
        useSelector((state) => state.orderTotals?.['SUB TOTAL AFTER ADJUSTMENT']) || 0;
    const amountShortForShippingDiscount =
        useSelector((state) => state.cartDetails?.data?.amountShortForShippingDiscount) || 0;
    const {
        enableNewOrderSummaryRedesign = true,
        enableShippingThresholdBanner = true,
        enableShippingThresholdProgressBar = true,
        enableFSHBanner = false,
    } = useSelector(selectFeatureFlags);
    const newBrdrStyles =
        enableNewOrderSummaryRedesign &&
        dt(['border-b', 'border-solid', 'border-gray-15', 'empty:hidden']);
    return (
        <div
            className={dt([
                'bg-light-white',
                'visibleSmMd',
                'smMd:-mx-6',
                'smMd:mt-4',
                'smMd:mb-6',
            ])}
        >
            <div className={dt(['sm12', 'md6', '!flex', 'flex-col', 'mx-auto', '!px-0'])}>
                <div className={cx(newBrdrStyles)}>
                    {isNotAGiftCardOrder && !isDesktop && handleApplyRewardsSection()}
                </div>
                {enableNewOrderSummaryRedesign && (
                    <div
                        className={dt([
                            'text-xlarge',
                            'font-bold',
                            'bg-light-white',
                            isNotAGiftCardOrder ? 'pt-4' : 'pt-6',
                            'pb-3',
                            'smOnly:px-6',
                            'mdOnly:px-2',
                            'lg:rounded-t-lg',
                        ])}
                    >
                        Order Summary
                    </div>
                )}
                <div
                    className={cx('visibleSmMd', 'orderSummaryRedesign', 'orderSummaryWrapper')}
                    data-automation-id="at-ordersummarywrapper-small"
                >
                    <div
                        className={`${cx('orderSummaryNew')} ${dt([
                            'mb-4',
                            'smOnly:mb-1',
                            'bg-white',
                        ])}`}
                    >
                        {displayShippingThresholdHeader && enableShippingThresholdBanner && (
                            <div
                                className={cx(
                                    amountShortForShippingDiscount > 0 || isShippingFree
                                        ? 'shipping-threshold-msg'
                                        : ''
                                )}
                            >
                                <div
                                    className={dt([
                                        'w-full',
                                        enableNewOrderSummaryRedesign ? 'smOnly:px-6' : 'px-2',
                                        enableNewOrderSummaryRedesign && 'mdOnly:px-2',
                                    ])}
                                >
                                    <ShippingThresholdHeader
                                        orderTotal={orderTotal}
                                        shippingShortTotal={amountShortForShippingDiscount}
                                        isShippingFree={isShippingFree}
                                        freeShippingThreshold={freeShippingThreshold}
                                        customClass={cx('cartShippingThreshold')}
                                        enableShippingThresholdProgressBar={
                                            enableShippingThresholdProgressBar
                                        }
                                        enableNewOrderSummaryRedesign={
                                            enableNewOrderSummaryRedesign
                                        }
                                        enableFSHBanner={enableFSHBanner}
                                        renderFrom="cartOrderSummary"
                                        irisShippingThresholds={irisShippingThresholds}
                                    />
                                </div>
                            </div>
                        )}
                        <div
                            className={`${cx(
                                'OrderSummaryNewUI',
                                'rewardsSection',
                                !isNotAGiftCardOrder ? 'fullBorderRadius' : ''
                            )} ${dt([
                                'border-t-0',
                                'border-b-0',
                                'smMd:mx-auto',
                                'smMd:my-0',
                                'mdOnly:w-full',
                                'mdOnly:!px-2',
                                // eslint-disable-next-line no-nested-ternary
                                enableNewOrderSummaryRedesign
                                    ? enableShippingThresholdBanner &&
                                      !displayShippingThresholdHeader
                                        ? '!pt-0'
                                        : '!pt-3'
                                    : '',
                                enableNewOrderSummaryRedesign && 'sm:!pb-6',
                                enableNewOrderSummaryRedesign && 'lg:!pb-5',
                            ])}`}
                        >
                            <OrderSummary
                                isCart
                                enableNewOrderSummaryRedesign={enableNewOrderSummaryRedesign}
                                enableApplePay={applePayEligible}
                                removeCouponSummary={removeCouponSummary}
                                idSuffix={idSuffix}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

OrderSummaryWrapperMobile.propTypes = {
    isNotAGiftCardOrder: PropTypes.bool,
    handleApplyRewardsSection: PropTypes.func,
    displayShippingThresholdHeader: PropTypes.bool,
    isShippingFree: PropTypes.bool,
    removeCouponSummary: PropTypes.bool,
    idSuffix: PropTypes.string,
};

OrderSummaryWrapperMobile.defaultProps = {
    isNotAGiftCardOrder: true,
    handleApplyRewardsSection: () => {},
    displayShippingThresholdHeader: true,
    isShippingFree: false,
    removeCouponSummary: false,
    idSuffix: '',
};

export default OrderSummaryWrapperMobile;
